import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { cookieManager } from '../utils/cookieManager';
import { ADS_ENABLED } from '../config/ads';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldLoadAdSense, setShouldLoadAdSense] = useState(false);
  
  // Get environment-specific GA ID
  const GA_ID = (() => {
    if (process.env.NODE_ENV === 'production') {
      return process.env.REACT_APP_GA_MEASUREMENT_ID;
    }
    // Development environment
    return process.env.REACT_APP_GA_MEASUREMENT_ID;
  })();

  const loadAnalytics = useCallback(() => {
    // Check if analytics is already loaded and configured
    if (window.dataLayer && window.gtag) {
      //console.log('Analytics already loaded, updating consent signals...');
      
      // Update consent signals to granted
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'analytics_storage': 'granted'
      });
      
      // Send consent signal to Microsoft Clarity
      if (window.clarity) {
        window.clarity("consent", "granted");
      }
      
      // Configure Google Analytics (if GA_ID exists and not already configured)
      if (GA_ID && !window.gaConfigured) {
        window.gtag('config', GA_ID, {
          debug_mode: process.env.NODE_ENV === 'development'
        });
        window.gaConfigured = true;
      }
      
      return;
    }

    // If gtag is not available yet, retry after a short delay
    //console.log('Analytics not loaded yet, retrying in 500ms...');
    setTimeout(() => {
      if (window.gtag) {
        loadAnalytics();
      } else {
        //console.log('gtag still not available after retry');
      }
    }, 500);
  }, [GA_ID]);

  const loadAdSense = useCallback(() => {
    if (!ADS_ENABLED) return;
    // Global lock to prevent simultaneous loading attempts
    if (window.adsenseLoading) {
      return;
    }

    // Check if Auto Ads already initialized - NEVER initialize twice
    if (window.adsenseAutoAdsInitialized) {
      return;
    }

    // Set loading lock
    window.adsenseLoading = true;

    // Update consent signals for Google (already initialized in App.js)
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'analytics_storage': 'granted'
      });
    }

    // Set flag to load AdSense via Helmet
    setShouldLoadAdSense(true);
  }, []);

  useEffect(() => {
    // Delay the cookie banner to ensure it doesn't affect LCP
    const showBannerTimeout = setTimeout(() => {
      const hasConsented = cookieManager.hasConsent('cookieConsent');
      const hasWithdrawn = cookieManager.hasWithdrawnConsent('cookieConsent');
      
      // Show banner if no consent decision was made OR if consent was explicitly withdrawn
      if (!cookieManager.hasMadeConsentDecision('cookieConsent') || hasWithdrawn) {
        setIsVisible(true);
      } else if (hasConsented) {
        loadAnalytics();
        const adsConsent = cookieManager.get('adsConsent');
        if (ADS_ENABLED && adsConsent === 'true') {
          //console.log('Existing ads consent found, loading AdSense...');
          setTimeout(() => {
            loadAdSense();
          }, 100);
        } else if (adsConsent === 'false') {
          // Update consent signals if user previously declined
          if (window.gtag) {
            window.gtag('consent', 'update', {
              'ad_storage': 'denied',
              'ad_user_data': 'denied', 
              'ad_personalization': 'denied',
              'analytics_storage': 'granted'
            });
          }
        }
      }
    }, 2000); // 2 second delay

    return () => clearTimeout(showBannerTimeout);
  }, [loadAnalytics, loadAdSense]);

  // Add a listener for storage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'cookieConsent') {
        if (e.newValue === 'true') {
          loadAnalytics();
          setIsVisible(false);
        } else if (e.newValue === 'false') {
          // User withdrew consent, show banner again
          setIsVisible(true);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadAnalytics]);

  const handleAccept = useCallback(() => {
    //console.log('User accepted cookies - initializing services...');
    cookieManager.set('cookieConsent', 'true');
    cookieManager.set('adsConsent', 'true');
    cookieManager.set('clarityConsent', 'true');
    setIsVisible(false);
    loadAnalytics();
    
    // Add small delay to prevent race conditions
    setTimeout(() => {
      loadAdSense();
    }, 100);
  }, [loadAnalytics, loadAdSense]);

  const handleDecline = () => {
    cookieManager.set('cookieConsent', 'false');
    cookieManager.set('adsConsent', 'false');
    cookieManager.set('clarityConsent', 'false');
    
    // Set consent signals for Google when declined
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'analytics_storage': 'denied'
      });
    }
    
    // Send consent signal to Microsoft Clarity
    if (window.clarity) {
      window.clarity("consent", "denied");
    }
    
    setIsVisible(false);
  };

  // Function to withdraw consent (can be called from other components)
  const withdrawConsent = useCallback(() => {
    cookieManager.set('cookieConsent', 'false');
    cookieManager.set('adsConsent', 'false');
    cookieManager.set('clarityConsent', 'false');
    
    // Update Google consent signals to denied
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'analytics_storage': 'denied'
      });
    }
    
    // Send consent signal to Microsoft Clarity
    if (window.clarity) {
      window.clarity("consent", "denied");
    }
    
    // Remove AdSense scripts (Google Tag remains but with denied consent)
    const existingAdSenseScripts = document.querySelectorAll('script[src*="googlesyndication.com"]');
    existingAdSenseScripts.forEach(script => script.remove());
    
    // Reset AdSense global variables
    window.adsenseAutoAdsInitialized = false;
    window.adsenseScriptLoaded = false;
    window.adsenseLoading = false;
    window.gaConfigured = false;
    
    // Show consent banner again
    setIsVisible(true);
    
    //console.log('Consent withdrawn - tracking disabled, banner will show again');
  }, []);

  // Expose withdrawConsent globally for other components to use
  useEffect(() => {
    window.withdrawConsent = withdrawConsent;
    return () => {
      delete window.withdrawConsent;
    };
  }, [withdrawConsent]);

  if (!isVisible) return null;

  return (
    <>
      {shouldLoadAdSense && (
        <Helmet>
          <script
            key="adsense-script"
            async
            src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9128369558228541"
            crossOrigin="anonymous"
            onLoad={() => {
              // Clear loading lock
              window.adsenseLoading = false;
              
              // Double-check if Auto Ads already initialized (race condition protection)
              if (window.adsenseAutoAdsInitialized) {
                return;
              }

              // Mark as initialized BEFORE calling push to prevent race conditions
              window.adsenseAutoAdsInitialized = true;

              // Initialize Auto Ads with consent signal
              try {
                (window.adsbygoogle = window.adsbygoogle || []).push({
                  google_ad_client: 'ca-pub-9128369558228541',
                  enable_page_level_ads: true
                });
              } catch (error) {
                console.error('AdSense Auto Ads initialization error:', error);
                // Reset flags if initialization failed
                window.adsenseAutoAdsInitialized = false;
              }
            }}
            onError={() => {
              console.error('Failed to load AdSense script');
              window.adsenseAutoAdsInitialized = false;
              window.adsenseLoading = false;
            }}
          />
        </Helmet>
      )}
      <div 
      role="dialog"
      aria-modal="true"
      aria-label="Cookie Consent"
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50 md:text-base text-sm"
      style={{ 
        opacity: 0,
        animation: 'fadeIn 0.5s ease-in-out forwards',
        animationDelay: '0.1s',
        maxHeight: '100vh',
        overflowY: 'auto'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1">
            <p className="text-gray-600">
              LiveLoveSpain uses cookies and serves ads to enhance your experience. By continuing, you agree to our use of cookies and personalized ads.{' '}
              <a 
                href="/cookies" 
                className="text-blue-600 hover:text-blue-700 underline"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/cookies';
                }}
              >
                Learn more
              </a>
            </p>
          </div>
          <div className="flex gap-3 w-full sm:w-auto">
            <button
              onClick={handleDecline}
              className="flex-1 sm:flex-none px-4 py-2 text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              className="flex-1 sm:flex-none px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
    </>
  );
};

export default CookieConsent; 